import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import { MultiStyleType } from '@chakra-ui/styled-system'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { textStyles } from '~/themes/textStyles/defaultTextStyles'
import { getName } from '~/utils/objectUtils'

import { phoneColorKeys } from './_colors'

const { Icon: DefaultIconColor } = themeColorSectionKeys.defaults
const { ActiveCallBlock: CallBlockColors } = phoneColorKeys.Phone

export enum CallBlockVariants {
  Ringing = 'ringing',
  Connected = 'connected',
  OnHold = 'onHold',
}

const CallBlockParts = [
  'container',
  'statusBadge',
  'number',
  'controlBar',
  'controlButton',
  'endCallButton',
  'endCallIcon',
  'muteButton',
  'muteIcon',
  'holdButton',
  'holdIcon',
  'switchButton',
  'switchIcon',
  'transferButton',
  'transferIcon',
] as const

const helpers = createMultiStyleConfigHelpers(CallBlockParts)

export const CallBlockStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      flexDirection: 'column',
      borderBottom: '1px solid',
      borderColor: CallBlockColors.CallBlockHeader.Border,
      gap: '8px',
      paddingY: '20px',
      alignItems: 'flex-start',
    },
    statusBadge: {
      display: 'flex',
      width: '130px',
      maxWidth: 'full',
      transition: 'width 0.25s ease-out',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '24px',
      background: CallBlockColors.CallBlock.StatusBadge.Background.Inactive,
      paddingY: '4px',
      paddingX: '12px',
      ...textStyles[textStyleKeys.Body3_Bold],
    },
    number: {
      ...textStyles[textStyleKeys.Body2],
    },
    controlBar: {
      alignItems: 'flex-start',
      gap: '8px',
      paddingTop: '4px',
    },
    controlButton: {
      boxSize: '28px',
      minWidth: '0',
      borderRadius: 'full',
      background: CallBlockColors.CallBlock.ControlButtons.Background.Default,
    },
    endCallButton: {
      background:
        CallBlockColors.CallBlock.ControlButtons.EndCall.Background.Default,
      _hover: {
        background:
          CallBlockColors.CallBlock.ControlButtons.EndCall.Background.Hover,
      },
    },
    endCallIcon: {
      boxSize: '20px',
      color: CallBlockColors.CallBlock.ControlButtons.EndCall.Icon,
    },
    muteButton: {
      background: CallBlockColors.CallBlock.ControlButtons.Background.Default,
      _hover: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
      _active: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
    },
    muteIcon: {
      boxSize: '24px',
      color: DefaultIconColor,
    },
    holdButton: {
      background: CallBlockColors.CallBlock.ControlButtons.Background.Default,
      _hover: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
      _active: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
    },
    holdIcon: {
      boxSize: '24px',
      color: DefaultIconColor,
    },
    switchButton: {
      background: CallBlockColors.CallBlock.ControlButtons.Background.Default,
      _hover: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
      _active: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
    },
    switchIcon: {
      boxSize: '28px',
      color: DefaultIconColor,
    },
    transferButton: {
      background: CallBlockColors.CallBlock.ControlButtons.Background.Default,
      _hover: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
      _active: {
        background: CallBlockColors.CallBlock.ControlButtons.Background.Hover,
        '& > svg': {
          color: CallBlockColors.CallBlock.ControlButtons.Icon.Hover,
        },
      },
    },
    transferIcon: {
      boxSize: '24px',
      color: DefaultIconColor,
    },
  },
  variants: {
    [CallBlockVariants.Ringing]: {
      statusBadge: {},
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
    [CallBlockVariants.Connected]: {
      statusBadge: {
        '@keyframes color': {
          from: {
            background:
              CallBlockColors.CallBlock.StatusBadge.Background.Active
                .Keyframe_From,
          },
          to: {
            background:
              CallBlockColors.CallBlock.StatusBadge.Background.Active
                .Keyframe_To,
          },
        },
        animation: 'color 1s infinite alternate',
        color: CallBlockColors.CallBlock.StatusBadge.FontColor.Active,
      },
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
    [CallBlockVariants.OnHold]: {
      statusBadge: {
        width: '72px',
      },
      number: {},
      endCallIcon: {},
      muteIcon: {},
      holdIcon: {},
      switchIcon: {},
    },
  },
  defaultProps: {},
})

export const useCallBlockStyle = (props?: StyleThemingProps) => {
  return useMultiStyleConfig(
    getName({ CallBlockStyle }),
    props,
  ) as MultiStyleType<typeof CallBlockParts>
}
